import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import { Grid } from "../Grid";
import { Select } from "../Select";
import { TextField } from "../TextField";
import { textInputBaseClasses } from "../TextInputBase";
import { Typography } from "../Typography";
import { UnfoldMoreOutline } from "../../icons/icons";
import { SlotsProvider } from "../../providers/SlotsProvider";
import { mergeSlotProps } from "../../providers/utils/slots";
import { convertToTwoDecimalNumber } from "./utils";
export const colorPickerTextFieldsSlots = {
    colorValueField: TextField,
    colorFormatSelector: Select,
};
export const ColorPickerTextFields = ({ color, updateColor, slots, slotProps, }) => {
    const colorFormatOptions = ["Hex", "Hsl", "Hsv", "Rgb"];
    const [selectedColorFormat, setColorFormat] = useState("Hex");
    const [alpha, setAlpha] = useState(Number((color.rgb.a * 100).toFixed(0)));
    const [hex, setHex] = useState(color.hex);
    const [hsla, setHsla] = useState({
        h: convertToTwoDecimalNumber(color.hsl.h),
        s: convertToTwoDecimalNumber(color.hsl.s),
        l: convertToTwoDecimalNumber(color.hsl.l),
        a: convertToTwoDecimalNumber(color.hsl.a),
    });
    const [hsva, setHsva] = useState({
        h: convertToTwoDecimalNumber(color.hsv.h),
        s: convertToTwoDecimalNumber(color.hsv.s),
        v: convertToTwoDecimalNumber(color.hsv.v),
        a: convertToTwoDecimalNumber(color.hsv.a),
    });
    const [rgba, setRgba] = useState({
        r: color.rgb.r,
        g: color.rgb.g,
        b: color.rgb.b,
        a: color.rgb.a,
    });
    const isHex = selectedColorFormat === "Hex";
    const isHsla = selectedColorFormat === "Hsl";
    const isHsva = selectedColorFormat === "Hsv";
    const isRgba = selectedColorFormat === "Rgb";
    useEffect(() => {
        setHex(color.hex);
        setHsla({
            h: convertToTwoDecimalNumber(color.hsl.h),
            s: convertToTwoDecimalNumber(color.hsl.s),
            l: convertToTwoDecimalNumber(color.hsl.l),
            a: convertToTwoDecimalNumber(color.hsl.a),
        });
        setHsva({
            h: convertToTwoDecimalNumber(color.hsv.h),
            s: convertToTwoDecimalNumber(color.hsv.s),
            v: convertToTwoDecimalNumber(color.hsv.v),
            a: convertToTwoDecimalNumber(color.hsv.a),
        });
        setRgba(color.rgb);
        setAlpha(Number((color.rgb.a * 100).toFixed(0)));
    }, [color]);
    return (_jsx(SlotsProvider, { baseSlots: colorPickerTextFieldsSlots, slots: slots, children: ({ colorValueField: ColorValueField, colorFormatSelector: ColorFormatSelector }) => (_jsxs(Grid, { container: true, gap: 2, children: [_jsx(Grid, { item: true, xs: true, children: _jsx(ColorFormatSelector, { onChange: (value) => value ? setColorFormat(value) : setColorFormat("Hex"), options: colorFormatOptions.map((colorFormat) => ({
                            label: colorFormat,
                        })), fullWidth: true, value: selectedColorFormat, endNode: _jsx(UnfoldMoreOutline, { color: "disabled" }), ...slotProps?.colorFormatSelector, sx: {
                            cursor: "pointer",
                            caretColor: "transparent",
                            ...slotProps?.colorFormatSelector?.sx,
                        }, slotProps: mergeSlotProps({
                            inputRoot: {
                                sx: {
                                    paddingX: 1.5,
                                    [`& .${textInputBaseClasses.input}`]: {
                                        minHeight: 6.5,
                                    },
                                },
                            },
                        }, slotProps?.colorFormatSelector?.slotProps) }) }), isHex && (_jsx(Grid, { item: true, xs: true, children: _jsx(ColorValueField, { fullWidth: true, value: hex, startNode: _jsx(Typography, { variant: "body3", color: "disabled", children: "#" }), onChange: (_, v) => {
                            if (!v) {
                                setHex("");
                            }
                            if (v && v.length < 6) {
                                setHex(v);
                            }
                            if (v && v.length >= 6 && v.length < 7) {
                                updateColor(v);
                            }
                        }, ...slotProps?.colorValueField, sx: {
                            minWidth: 21,
                            ...slotProps?.colorValueField?.sx,
                        }, slotProps: mergeSlotProps({
                            inputRoot: {
                                sx: {
                                    paddingX: 1.5,
                                    [`& .${textInputBaseClasses.input}`]: {
                                        minHeight: 6.5,
                                    },
                                },
                            },
                        }, slotProps?.colorValueField?.slotProps) }) })), isHsla &&
                    ["h", "s", "l"].map((letter, index) => (_jsx(Grid, { item: true, xs: true, children: _jsx(ColorValueField, { fullWidth: true, value: Object.values(hsla)[index], startNode: _jsx(Typography, { variant: "body3", color: "disabled", sx: {
                                    textTransform: "capitalize",
                                }, children: letter }), onChange: (_, v) => {
                                setHsla({
                                    ...hsla,
                                    [letter]: convertToTwoDecimalNumber(v ?? 0),
                                });
                                updateColor({
                                    ...hsla,
                                    [letter]: Number(v ?? 0),
                                });
                            }, ...slotProps?.colorValueField, slotProps: mergeSlotProps({
                                inputRoot: {
                                    sx: {
                                        paddingX: 1.5,
                                        [`& .${textInputBaseClasses.input}`]: {
                                            minHeight: 6.5,
                                        },
                                    },
                                },
                            }, slotProps?.colorValueField?.slotProps) }) }, letter))), isHsva &&
                    ["h", "s", "v"].map((letter, index) => (_jsx(Grid, { item: true, xs: true, children: _jsx(ColorValueField, { fullWidth: true, value: Object.values(hsva)[index], startNode: _jsx(Typography, { variant: "body3", color: "disabled", sx: {
                                    textTransform: "capitalize",
                                }, children: letter }), onChange: (_, v) => {
                                setHsva({
                                    ...hsva,
                                    [letter]: convertToTwoDecimalNumber(v ?? 0),
                                });
                                updateColor({
                                    ...hsva,
                                    [letter]: Number(v ?? 0),
                                });
                            }, ...slotProps?.colorValueField, slotProps: mergeSlotProps({
                                inputRoot: {
                                    sx: {
                                        paddingX: 1.5,
                                        [`& .${textInputBaseClasses.input}`]: {
                                            minHeight: 6.5,
                                        },
                                    },
                                },
                            }, slotProps?.colorValueField?.slotProps) }) }, letter))), isRgba &&
                    ["r", "g", "b"].map((letter, index) => (_jsx(Grid, { item: true, xs: true, children: _jsx(ColorValueField, { fullWidth: true, value: Object.values(rgba)[index], startNode: _jsx(Typography, { variant: "body3", color: "disabled", sx: {
                                    textTransform: "capitalize",
                                }, children: letter }), onChange: (_, v) => {
                                if (v) {
                                    setRgba({ ...rgba, [letter]: Number(v) });
                                    updateColor({ ...rgba, [letter]: Number(v) });
                                }
                            }, ...slotProps?.colorValueField, slotProps: mergeSlotProps({
                                inputRoot: {
                                    sx: {
                                        paddingX: 1.5,
                                        [`& .${textInputBaseClasses.input}`]: {
                                            minHeight: 6.5,
                                        },
                                    },
                                },
                            }, slotProps?.colorValueField?.slotProps) }) }, letter))), _jsx(Grid, { item: true, xs: true, children: _jsx(ColorValueField, { fullWidth: true, value: `${alpha}`, onChange: (_, v) => {
                            setAlpha(Number(v));
                            updateColor({
                                ...color.rgb,
                                a: Number(v ?? 0) / 100,
                            });
                        }, ...slotProps?.colorValueField, sx: {
                            minWidth: 12,
                            ...slotProps?.colorValueField?.sx,
                        }, endNode: _jsx(Typography, { variant: "body3", color: "disabled", children: "%" }), slotProps: mergeSlotProps({
                            inputRoot: {
                                sx: {
                                    paddingX: 1.5,
                                    [`& .${textInputBaseClasses.input}`]: {
                                        minHeight: 6.5,
                                    },
                                },
                            },
                        }, slotProps?.colorValueField?.slotProps) }) })] })) }));
};
//# sourceMappingURL=ColorPickerTextFields.js.map